import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Eye, Download, Contact, Plus, Users, Wallet, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="iw-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g,"-")}`}>
    <div className="flex items-center justify-between">
      <span className="overline">{label}</span>
      <Icon size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
    </div>
    <p className="font-display text-4xl font-bold mt-3">{value}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ cards: 0, views: 0, saves: 0, leads: 0, client_accounts: 0 });
  const [hasCustomer, setHasCustomer] = useState(false);
  const [porting, setPorting] = useState(false);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/billing").then((r) => setHasCustomer(!!r.data.has_customer)).catch(() => {}); }, []);

  const openPortal = async () => {
    setPorting(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billing portal is not available yet.");
      setPorting(false);
    }
  };

  return (
    <DashboardLayout title="Dashboard"
      actions={<div className="flex items-center gap-2">
        {hasCustomer && (
          <button onClick={openPortal} disabled={porting} data-testid="dashboard-manage-billing-btn"
            className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2 disabled:opacity-60">
            <Wallet size={16} /> {porting ? "Opening…" : "Manage Billing"} {!porting && <ExternalLink size={13} />}
          </button>
        )}
        <Link to="/app/cards/new" className="btn-gold px-4 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="new-card-btn"><Plus size={16} /> New Card</Link>
      </div>}>
      <div className="fade-up">
        <p className="text-[#8B9BB4] mb-6">Welcome back, <span className="text-[#F6F1E4] font-semibold">{user?.name}</span>. Here's your network at a glance.</p>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat icon={CreditCard} label="Cards" value={stats.cards} />
          <Stat icon={Eye} label="Total Views" value={stats.views} />
          <Stat icon={Download} label="Contact Saves" value={stats.saves} />
          <Stat icon={Contact} label="Leads" value={stats.leads} />
          <Stat icon={Users} label="Client Accounts" value={stats.client_accounts} />
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <Link to="/app/cards/new" className="iw-card p-6 block"><Plus className="text-[#D4AF37] mb-3" /><h3 className="font-display text-lg font-bold">Create a Card</h3><p className="text-sm text-[#8B9BB4] mt-1">Build a new digital business card in minutes.</p></Link>
          <Link to="/app/clients" className="iw-card p-6 block"><Users className="text-[#D4AF37] mb-3" /><h3 className="font-display text-lg font-bold">Client Accounts</h3><p className="text-sm text-[#8B9BB4] mt-1">Manage teams, seats and brand identity.</p></Link>
          <Link to="/app/studio" className="iw-card p-6 block"><CreditCard className="text-[#D4AF37] mb-3" /><h3 className="font-display text-lg font-bold">QR & NFC Studio</h3><p className="text-sm text-[#8B9BB4] mt-1">Export QR codes and program NFC tags.</p></Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
